import type { LucideIcon } from "lucide-react";
import { formatPocet } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface MenuPolozkaProps {
  ikona: LucideIcon;
  nazov: string;
  pocet: number | undefined;
  aktivna: boolean;
  /**
   * `current` pre kategórie (miesto v zozname, `aria-current`), `pressed`
   * pre stav a archív (prepínač filtra, `aria-pressed`).
   */
  oznacenie: "current" | "pressed";
  /** Riadok zákazky je nad cieľom ťahania — kam padne. */
  ciel?: boolean;
  /** Pri prejdení myšou počet ustúpi tlačidlu ⋮ kategórie. */
  skryPocetPriHover?: boolean;
  /** Položka bez počtu (odkaz na nastavenia v menu Cenníka, KOLO 199). */
  bezPoctu?: boolean;
  title?: string;
  onClick: () => void;
}

/**
 * Položka ľavého menu prehľadu zákaziek (KOLO 174, „menu 1") — ikona,
 * názov a počet v kapsuli. Vybraná je ružová kapsula, rovnaká ako
 * aktívna položka hlavičky, takže menu a navigácia hovoria jedným jazykom.
 */
export function MenuPolozka({
  ikona: Ikona,
  nazov,
  pocet,
  aktivna,
  oznacenie,
  ciel = false,
  skryPocetPriHover = false,
  bezPoctu = false,
  title,
  onClick,
}: MenuPolozkaProps): JSX.Element {
  return (
    <button
      type="button"
      aria-current={oznacenie === "current" && aktivna ? "page" : undefined}
      aria-pressed={oznacenie === "pressed" ? aktivna : undefined}
      title={title}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-full border border-transparent py-1.5 pl-3 pr-2 text-left font-ui text-sm transition-colors",
        aktivna
          ? "bg-tandem-red-light font-semibold text-tandem-red-dark"
          : "font-medium text-tandem-gray-dark hover:bg-muted",
        ciel && "border-dashed border-primary bg-tandem-red-light",
      )}
    >
      <Ikona aria-hidden className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{nazov}</span>
      {bezPoctu ? null : (
        <span
          className={cn(
            "min-w-[1.5rem] shrink-0 rounded-full px-1.5 text-center font-mono text-[11px] font-bold leading-5 tabular-nums",
            aktivna ? "bg-white text-tandem-red-dark" : "bg-muted text-tandem-gray-dark",
            skryPocetPriHover && "group-focus-within:invisible group-hover:invisible",
          )}
        >
          {pocet === undefined ? "–" : formatPocet(pocet)}
        </span>
      )}
    </button>
  );
}

/** Nadpis malej sekcie menu (`stitok-sekcie` zo systému dizajnu). */
export function MenuStitok({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="px-3 font-ui text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}
